import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { IconButton, Search } from 'basic-components';
import { Clear } from 'basic-components/icons';
import Query from 'dashboard-services/api/Query';
import { loginActions } from 'primary-components';

import { filesDatalakeActions } from 'actions/modules/datalake';
import { basicPipelineDashboardActions } from 'actions/modules/datascience/dashboard';
import { searchActions } from 'actions/modules/tools';
import getApiConfig from 'api/ApiConfig';
import { companyAuthQueries, roleAuthQueries, userAuthQueries } from 'queries/modules/auth';
import { filesDatalakeQueries, groupsDatalakeQueries } from 'queries/modules/datalake';
import { pipelinesQueries, tasksQueries } from 'queries/modules/datascience';
import { dataStorageQueries } from 'queries/modules/datastorage';
import { formulaQueries } from 'queries/modules/formula';
import { insightQueries } from 'queries/modules/insight';
import { statusesQueries } from 'queries/modules/statuses';
import { routing } from 'routing';

import { Recents } from './Recents';
import ResultBlock from './ResultBlock';

import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { When } from 'react-if';

import classNames from 'classnames';
import './SearchAll.scss';

const useItemRenderer = ({ identifier, identifierField, mainField, secondaryField, navigate, setExpandedFalse, getUrl }) => {
  const renderer = useCallback((item, index) => (
    <a 
        className="search-all__content__results__block__body__item"
        href={`${window.location.protocol}//${window.location.host}${getUrl(item)}`}
        key={identifier + (identifierField ? item[identifierField] : index)}
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          navigate(getUrl(item))
          setExpandedFalse()
        }}
    >
      <div className="search-all__content__results__block__body__item__name">{typeof mainField === "function" ? mainField(item) : item[mainField]}</div>
      <When condition={secondaryField}>
        {() => (
          <div className="search-all__content__results__block__body__item__group">{typeof secondaryField === "function" ? secondaryField(item) : item[secondaryField]}</div>
        )}
      </When>
    </a>
  ), [setExpandedFalse, navigate, mainField, secondaryField, getUrl, identifier, identifierField])

  return renderer
}

const SIZE = 5, emptyArray = []
const wildcardQuery = query => `*${query}*`
const getFormulaName = item => String(item?.fileName).replace(/\.json$/, "")
const SearchAll = () => {
  const dispatch = useDispatch(),
        navigate = useNavigate(),
        { searchAllOpen: expanded, searchAllQuery } = useSelector(state => state.toolsState.searchState),
        storageScreen = useSelector(state => state.myAccountState.settingsState.storageScreen),
        [query, setQuery] = useState(""),
        [searchParams] = useSearchParams(),
        provider = useMemo(() => searchParams.get("provider") || undefined, [searchParams]),
        getQueryBuilderWithProvider = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withFetching(false)
            .withHeader("Target", provider, true)
        , [dispatch, provider]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withFetching(false)
        , [dispatch]),
        getRunQueryBuilder = useCallback(() =>
          getQueryBuilder()
            .withShouldDisplayError(false)
        , [getQueryBuilder]),
        onPageSelect = useCallback(response => response?.pages?.[0]?.items || emptyArray, []),
        filesAreVisible = useMemo(() => dispatch(loginActions.hasAccessTo(routing.dataLake.routing.files.name, "R", routing.dataLake.name)), [dispatch]),
        { isPending: filesAreFetching, data: files = emptyArray } = filesDatalakeQueries.useSearchFiles({ query: filesDatalakeActions.isUuidQuery(query) ? `uuid=${query}` : (String(query).includes("=") ? query : wildcardQuery(query)), provider, shouldRefetch: false, enabled: filesAreVisible && expanded && !!query, size: SIZE, getQueryBuilder: getQueryBuilderWithProvider, onSelect: onPageSelect, refetchOnWindowFocus: false }),
        
        groupsAreVisible = useMemo(() => dispatch(loginActions.hasAccessTo(routing.dataLake.routing.groups.name, "R", routing.dataLake.name)), [dispatch]),
        { isPending: groupsAreFetching, data: groups = emptyArray } = groupsDatalakeQueries.useSearchGroups({ query, size: SIZE, provider, shouldRefetch: false, getQueryBuilder, enabled: groupsAreVisible && expanded && !!query, onSelect: onPageSelect, refetchOnWindowFocus: false }),
        
        symbolsAreVisible = useMemo(() => 
          dispatch(loginActions.hasAccessTo(routing.dataStorage.routing.creator.name, "R", routing.dataStorage.name))
          ||
          dispatch(loginActions.hasAccessTo(routing.dataStorage.routing.oldCreator.name, "R", routing.dataStorage.name))
        , [dispatch]),
        { isPending: symbolsAreFetching, data: symbols = emptyArray } = dataStorageQueries.useSearchSymbols({ query: query ? `${query}*` : "", size: SIZE, getQueryBuilder: getQueryBuilderWithProvider, shouldRefetch: false, provider, enabled: symbolsAreVisible && expanded && !!query, select: onPageSelect, refetchOnWindowFocus: false }),

        dataPrepsAreVisible = useMemo(() => dispatch(loginActions.hasAccessTo(routing.dataStorage.routing.list.name, "R", routing.dataStorage.name)), [dispatch]),
        { isPending: dataPrepsAreFetching, data: dataPreps = emptyArray } = dataStorageQueries.useSearchDataPreps({ query: String(query).includes("=") ? query : "name=" + wildcardQuery(query), getQueryBuilder, shouldRefetch: false, enabled: dataPrepsAreVisible && expanded && !!query, latest: true, size: SIZE, select: onPageSelect, refetchOnWindowFocus: false }),

        pipelinesAreVisible = useMemo(() => dispatch(loginActions.hasAccessTo(routing.dataScience.routing.list.hashes.pipelines, "R", routing.dataScience.name)), [dispatch]),
        { isPending: pipelinesAreFetching, data: pipelines = emptyArray } = pipelinesQueries.useSearchPipelines({ query: String(query).includes("=") ? query : `*${query}*`, getQueryBuilder, shouldRefetch: false, latest: true, size: SIZE, enabled: pipelinesAreVisible && expanded && !!query, select: onPageSelect, refetchOnWindowFocus: false }),

        tasksAreVisible = useMemo(() => dispatch(loginActions.hasAccessTo(routing.dataScience.routing.task.name, "R", routing.dataScience.name)), [dispatch]),
        { isPending: tasksAreFetching, data: tasks = emptyArray } = tasksQueries.useSearchTasks({ query, getQueryBuilder, size: SIZE, onSelect: onPageSelect, shouldRefetch: false, enabled: tasksAreVisible && expanded && !!query, refetchOnWindowFocus: false }),

        // A formula opens on the Monitor, so it needs access to it. The `name` filter is an exact match, so a partial name goes through `query`.
        formulasAreVisible = useMemo(() => dispatch(loginActions.hasAccessTo(routing.dataScience.routing.dashboard.name, "R", routing.dataScience.name)), [dispatch]),
        { isPending: formulasAreFetching, data: formulas = emptyArray } = formulaQueries.useSearchFormulas({ query: String(query).includes("=") ? query : `name.keyword=${wildcardQuery(query)}`, getQueryBuilder, latest: true, size: SIZE, enabled: formulasAreVisible && expanded && !!query, onSelect: onPageSelect, shouldRefetch: false, refetchOnWindowFocus: false }),
        // A formula opens its latest run, so each result's latest run is looked up for its link. Without it the link opens
        // the run history instead, so a failed lookup shows no error.
        formulaIdentities = useMemo(() => formulas.map(item => ({ groupName: item.groupName, name: getFormulaName(item) })), [formulas]),
        { data: formulaRuns, isLoading: formulaRunsAreLoading } = statusesQueries.useGetLatestFormulaRuns({ formulas: formulaIdentities, getQueryBuilder: getRunQueryBuilder, enabled: expanded, refetchOnWindowFocus: false }),
        formulaRunByFid = useMemo(() => new Map(formulas.map((item, index) => [item.fid, formulaRuns[index]])), [formulaRuns, formulas]),
        // The block stays hidden until the runs are known, as it does while its search loads, so no link opens the wrong page.
        formulaResults = formulaRunsAreLoading ? emptyArray : formulas,
        

        insightAreVisible = useMemo(() => dispatch(loginActions.hasAccessTo(routing.insight.name, "R")), [dispatch]),
        { isPending: insightsAreFetching, data: insights = emptyArray } = insightQueries.useSearchInsights({ query: query + "&fields.isDraft=false", size: SIZE, getQueryBuilder, onSelect: onPageSelect, enabled: insightAreVisible && expanded && !!query, shouldRefetch: false, refetchOnWindowFocus: false }),

        usersAreVisible = useMemo(() => dispatch(loginActions.hasAccessTo(routing.myAccount.hashes.companyMgmtEntitlementName, "R", routing.myAccount.name)), [dispatch]),
        { isPending: usersAreFetching, data: users = emptyArray } = userAuthQueries.useSearchUsers({ query, size: SIZE, enabled: usersAreVisible && expanded && !!query, select: onPageSelect, getQueryBuilder, shouldRefetch: false, refetchOnWindowFocus: false }),

        rolesAreVisible = useMemo(() => dispatch(loginActions.hasAccessTo(routing.myAccount.hashes.companyMgmtEntitlementName, "R", routing.myAccount.name)), [dispatch]),
        { isPending: rolesAreFetching, data: roles = emptyArray } = roleAuthQueries.useSearchRoles({ query, size: SIZE, getQueryBuilder, shouldRefetch: false, enabled: rolesAreVisible && expanded && !!query, select: onPageSelect, refetchOnWindowFocus: false }),

        companiesAreVisible = useMemo(() => dispatch(loginActions.hasAccessTo(routing.myAccount.hashes.companyMgmtEntitlementName, "R", routing.myAccount.name)), [dispatch]),
        { isPending: companiesAreFetching, data: companies = [] } = companyAuthQueries.useSearchCompanies({ query: wildcardQuery(query), size: SIZE, getQueryBuilder, shouldRefetch: false, enabled: companiesAreVisible && expanded && !!query, select: onPageSelect, refetchOnWindowFocus: false }),

        searchRef = useRef(),
        setExpandedFalse = useCallback(() => dispatch(searchActions.changeValue(false, 'searchAllOpen')), [dispatch]),
        cleanSearchAllQuery = useCallback(() => dispatch(searchActions.changeValue(undefined, 'searchAllQuery')), [dispatch]),

        getFileUrl = useCallback(item => DashboardLinkFactory.dataLake.file({ baseUrl: "", fileId: item.fid }), []),
        filesRenderer = useItemRenderer({ identifier: "SearchAllFile", identifierField: "fid", mainField: "fileName", secondaryField: "groupName", navigate, setExpandedFalse, getUrl: getFileUrl }),
        
        getGroupUrl = useCallback(item => DashboardLinkFactory.dataLake.group({ baseUrl: "", groupName: item.name }), []),
        groupsRenderer = useItemRenderer({ identifier: "SearchAllGroup", identifierField: "name", mainField: "name", navigate, setExpandedFalse, getUrl: getGroupUrl }),

        getSymbolUrl = useCallback(item => DashboardLinkFactory.dataStorage.newDataPrep({ baseUrl: "", groupName: item?.groupName, query: "", storageScreen: routing.dataStorage.routing.creator.hashes.table, symbol: Object.entries(item?.symbols || {}).map(([k, v]) => `${k}=${v}`).join("|") }), []),
        symbolMainField = useCallback(item => Object.entries(item?.symbols).map(([k, v]) => k + ":" + v).join("; "), []),
        symbolsRenderer = useItemRenderer({ identifier: "SearchAllSymbol", mainField: symbolMainField, secondaryField: "groupName", navigate, setExpandedFalse, getUrl: getSymbolUrl }),
        
        getDataPrepUrl = useCallback(item => DashboardLinkFactory.dataStorage.dataPrep({ baseUrl: "", groupName: item.groupName, name: item.name, storageScreen, version: item.version }), [storageScreen]),
        dataPrepsRenderer = useItemRenderer({ identifier: "SearchAllDataPrep", identifierField: "id", mainField: "name", secondaryField: "groupName", navigate, setExpandedFalse, getUrl: getDataPrepUrl }),
        
        getPipelineUrl = useCallback(item => DashboardLinkFactory.dataScience.pipeline({ baseUrl: "", groupName: item.groupName, pipelineName: item.name }), []),
        pipelinesRenderer = useItemRenderer({ identifier: "SearchAllPipeline", identifierField: "id", mainField: "name", secondaryField: "groupName", navigate, setExpandedFalse, getUrl: getPipelineUrl }),
        
        getTaskUrl = useCallback(item => DashboardLinkFactory.dataScience.task({ baseUrl: "", taskId: item.id }), []),
        tasksRenderer = useItemRenderer({ identifier: "SearchAllTask", identifierField: "id", mainField: "readableName", secondaryField: "category", navigate, setExpandedFalse, getUrl: getTaskUrl }),

        getFormulaUrl = useCallback(item => basicPipelineDashboardActions.getFormulaMonitorUrl({ baseUrl: "", groupName: item.groupName, latestRun: formulaRunByFid.get(item.fid), name: getFormulaName(item) }), [formulaRunByFid]),
        formulasRenderer = useItemRenderer({ identifier: "SearchAllFormula", identifierField: "fid", mainField: getFormulaName, secondaryField: "groupName", navigate, setExpandedFalse, getUrl: getFormulaUrl }),

        insightMainField = useCallback(item => String(item?.fileName).replace(".json", ""), []),
        getInsightUrl = useCallback(item => DashboardLinkFactory.insight.view({ baseUrl: "", groupName: item.groupName, name: String(item.fileName).replace(".json", "") }), []),
        insightsRenderer = useItemRenderer({ identifier: "SearchAllInsight", identifierField: "fid", mainField: insightMainField, secondaryField: "groupName", navigate, setExpandedFalse, getUrl: getInsightUrl }),
        
        getUserUrl = useCallback(item => DashboardLinkFactory.myAccount.user({ baseUrl: "", email: item.email }), []),
        usersRenderer = useItemRenderer({ identifier: "SearchAllUser", identifierField: "email", mainField: "email", secondaryField: "companyName", navigate, setExpandedFalse, getUrl: getUserUrl }),
        
        getRoleUrl = useCallback(item => DashboardLinkFactory.myAccount.role({ baseUrl: "", roleId: item.egId }), []),
        rolesRenderer = useItemRenderer({ identifier: "SearchAllRole", identifierField: "name", mainField: "name", navigate, setExpandedFalse, getUrl: getRoleUrl }),
        
        getCompanyUrl = useCallback(item => DashboardLinkFactory.myAccount.company({ baseUrl: "", name: item.name }), []),
        companyRenderer = useItemRenderer({ identifier: "SearchAllCompany", identifierField: "name", mainField: "name", navigate, setExpandedFalse, getUrl: getCompanyUrl })

  useEffect(() => {
    expanded && searchRef.current.focus()
  }, [expanded])

  useEffect(() => {
    if(searchAllQuery !== undefined) {
      setQuery(searchAllQuery)
      cleanSearchAllQuery()
    }
  }, [searchAllQuery, cleanSearchAllQuery])
  
  return (
    <Fragment>
      <div className={classNames("search-all", { "search-all--is-expanded": expanded })}>
        <When condition={expanded}>
          {() => (
            <Fragment>
              <IconButton
                  className="search-all__close-button"
                  onClick={setExpandedFalse}
              >
                <Clear
                    color="grey-medium"
                    height={16}
                    width={16}
                />
              </IconButton>
              <div className="search-all__content">
                <div className="search-all__content__search">
                  <Search
                      defaultValue={query}
                      onSearch={setQuery}
                      placeholder="Search"
                      ref={searchRef}
                      size="large"
                      variant="outline"
                  />
                </div>
                <div className="search-all__content__results">
                  <When condition={query === ""}>
                    {() => (
                      <Recents
                          query={query}
                          setExpandedFalse={setExpandedFalse}
                      />
                    )}
                  </When>
                  <When condition={filesAreVisible && query !== ""}>
                    {() => (
                      <ResultBlock
                          closeSearch={setExpandedFalse}
                          isFetching={filesAreFetching}
                          itemRenderer={filesRenderer}
                          query={query}
                          results={files}
                          seeAllUrl={DashboardLinkFactory.dataLake.list({ baseUrl: "", query, tab: routing.dataLake.routing.files.name })}
                          title={provider ? `Files(${provider})` : "Files"}
                      />
                    )}
                  </When>
                  <When condition={groupsAreVisible && query !== ""}>
                    {() => (
                      <ResultBlock
                          closeSearch={setExpandedFalse}
                          isFetching={groupsAreFetching}
                          itemRenderer={groupsRenderer}
                          query={query}
                          results={groups}
                          seeAllUrl={DashboardLinkFactory.dataLake.list({ baseUrl: "", query, tab: routing.dataLake.routing.groups.name })}
                          title={provider ? `Groups(${provider})` : "Groups"}
                      />
                    )}
                  </When>
                  <When condition={symbolsAreVisible && query !== ""}>
                    {() => (
                      <ResultBlock
                          closeSearch={setExpandedFalse}
                          isFetching={symbolsAreFetching}
                          itemRenderer={symbolsRenderer}
                          query={query}
                          results={symbols}
                          seeAllUrl={DashboardLinkFactory.dataStorage.newDataPrep({ baseUrl: "", query, storageScreen: routing.dataStorage.routing.creator.hashes.table })}
                          title={provider ? `Symbols(${provider})` : "Symbols"}
                      />
                    )}
                  </When>
                  <When condition={dataPrepsAreVisible && query !== ""}>
                    {() => (
                      <ResultBlock
                          closeSearch={setExpandedFalse}
                          isFetching={dataPrepsAreFetching}
                          itemRenderer={dataPrepsRenderer}
                          query={query}
                          results={dataPreps}
                          seeAllUrl={DashboardLinkFactory.dataStorage.list({ baseUrl: "", query })}
                          title="Data preps"
                      />
                    )}
                  </When>
                  <When condition={pipelinesAreVisible && query !== ""}>
                    {() => (
                      <ResultBlock
                          closeSearch={setExpandedFalse}
                          isFetching={pipelinesAreFetching}
                          itemRenderer={pipelinesRenderer}
                          query={query}
                          results={pipelines}
                          seeAllUrl={DashboardLinkFactory.dataScience.list({ baseUrl: "", query, tab: routing.dataScience.routing.list.hashes.pipelines })}
                          title="Pipelines"
                      />
                    )}
                  </When>
                  <When condition={tasksAreVisible && query !== ""}>
                    {() => (
                      <ResultBlock
                          closeSearch={setExpandedFalse}
                          isFetching={tasksAreFetching}
                          itemRenderer={tasksRenderer}
                          query={query}
                          results={tasks}
                          seeAllUrl={DashboardLinkFactory.dataScience.list({ baseUrl: "", query, tab: routing.dataScience.routing.list.hashes.tasks })}
                          title="Tasks"
                      />
                    )}
                  </When>
                  <When condition={formulasAreVisible && query !== ""}>
                    {() => (
                      <ResultBlock
                          closeSearch={setExpandedFalse}
                          isFetching={formulasAreFetching || formulaRunsAreLoading}
                          itemRenderer={formulasRenderer}
                          query={query}
                          results={formulaResults}
                          seeAllUrl={DashboardLinkFactory.dataScience.monitor({ baseUrl: "", query, tab: routing.dataScience.routing.dashboard.hashes.formulas })}
                          title="Formulas"
                      />
                    )}
                  </When>
                  <When condition={insightAreVisible && query !== ""}>
                    {() => (
                      <ResultBlock
                          closeSearch={setExpandedFalse}
                          isFetching={insightsAreFetching}
                          itemRenderer={insightsRenderer}
                          query={query}
                          results={insights}
                          seeAllUrl={DashboardLinkFactory.insight.list({ baseUrl: "", query })}
                          title="Insights"
                      />
                    )}
                  </When>
                  <When condition={usersAreVisible && query !== ""}>
                    {() => (
                      <ResultBlock
                          closeSearch={setExpandedFalse}
                          isFetching={usersAreFetching}
                          itemRenderer={usersRenderer}
                          query={query}
                          results={users}
                          seeAllUrl={DashboardLinkFactory.myAccount.list({ baseUrl: "", query, tab: routing.myAccount.hashes.userMgmt })}
                          title="Users"
                      />
                    )}
                  </When>
                  <When condition={rolesAreVisible && query !== ""}>
                    {() => (
                      <ResultBlock
                          closeSearch={setExpandedFalse}
                          isFetching={rolesAreFetching}
                          itemRenderer={rolesRenderer}
                          query={query}
                          results={roles}
                          seeAllUrl={DashboardLinkFactory.myAccount.list({ baseUrl: "", query, tab: routing.myAccount.hashes.roleMgmt })}
                          title="Roles"
                      />
                    )}
                  </When>
                  <When condition={companiesAreVisible && query !== ""}>
                    {() => (
                      <ResultBlock
                          closeSearch={setExpandedFalse}
                          isFetching={companiesAreFetching}
                          itemRenderer={companyRenderer}
                          query={query}
                          results={companies}
                          seeAllUrl={DashboardLinkFactory.myAccount.list({ baseUrl: "", query, tab: routing.myAccount.hashes.companyMgmt })}
                          title="Companies"
                      />
                    )}
                  </When>
                </div>
              </div>
            </Fragment>
          )}
        </When>
      </div>
      <When condition={expanded}>
        {() => (
          <div
              className="search-all-background"
              onClick={setExpandedFalse}
          />
        )}
      </When>
    </Fragment>
  )
}

export default memo(SearchAll)
